/**
 * The default surface: the one AI surface (Gemini Enterprise, Copilot Studio, ...) the Surface
 * filter opens on, on every page that has the standard filters. Chosen under Lens, so a person who
 * works in one surface does not pick it again on each page. None by default (all surfaces).
 *
 * Stored as the surface registry's id (what the URL and the metrics carry), per browser.
 * Storage contract: localStorage DEFAULT_SURFACE_STORAGE_KEY holds the id, or is absent for none;
 * DEFAULT_SURFACE_EVENT on window announces a change in this tab ('storage' covers other tabs).
 */
import { useCallback, useEffect, useState } from 'react';

export const DEFAULT_SURFACE_STORAGE_KEY = 'saf3ai.lens.defaultSurface';
export const DEFAULT_SURFACE_EVENT = 'saf3ai:default-surface-changed';

export const readDefaultSurface = () => {
  try {
    return window.localStorage.getItem(DEFAULT_SURFACE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const writeDefaultSurface = (id) => {
  try {
    if (id) window.localStorage.setItem(DEFAULT_SURFACE_STORAGE_KEY, id);
    else window.localStorage.removeItem(DEFAULT_SURFACE_STORAGE_KEY);
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
  try {
    window.dispatchEvent(new CustomEvent(DEFAULT_SURFACE_EVENT, { detail: id }));
  } catch {
    // ignore
  }
};

/** useDefaultSurface() → { surfaceId, choose(id) }; choosing the current one clears it. */
export default function useDefaultSurface() {
  const [surfaceId, setSurfaceId] = useState(readDefaultSurface);

  useEffect(() => {
    const onChange = () => setSurfaceId(readDefaultSurface());
    const onStorage = (e) => { if (!e || e.key === null || e.key === DEFAULT_SURFACE_STORAGE_KEY) onChange(); };
    window.addEventListener(DEFAULT_SURFACE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(DEFAULT_SURFACE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const choose = useCallback((id) => {
    const next = id === readDefaultSurface() ? '' : id;
    writeDefaultSurface(next);
    setSurfaceId(next);
  }, []);

  return { surfaceId, choose };
}
